import { Injectable } from '@angular/core';
import {
  INPUT_VALUE_COVER_TYPE_SOFT,
  INPUT_VALUE_STEP_ANNIVERSARY_1,
  INPUT_VALUE_STEP_ANNIVERSARY_2,
  INPUT_VALUE_STEP_ANNIVERSARY_3,
  INPUT_VALUE_STEP_ANNIVERSARY_4,
  INPUT_VALUE_STEP_ANNIVERSARY_5,
  INPUT_VALUE_STEP_ANNIVERSARY_6,
  INPUT_VALUE_STEP_ANNIVERSARY_7,
  INPUT_VALUE_STEP_COVER,
  INPUT_VALUE_STEP_MESSAGE,
  INPUT_VALUE_STEP_PHOTO,
} from '@shared/common/product';
import { BookService } from '@studiobuki/web-core/lib/book-common';
import type { TCreateUserBookOmitKeys } from '@studiobuki/web-core/lib/firebase';
import { FirebaseService } from 'src/app/services/firebase.service';
import { Logger } from '@shared/common/logger';
import { LoaderService } from '@studiobuki/web-core/lib/loader';
import type { IBookFroebelkanMainichiKinenbiData } from '@shared/jp/models';
import {
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_0,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_1,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_2,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_3,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_4,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_5,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_6,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_7,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_8,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_9,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_10,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_11,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_12,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_13,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_14,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_15,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_16,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_17,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_18,
  PHOTO_FROEBELKAN_MAINICHI_KINENBI_19,
} from '@shared/jp/product';
import MainichiKinenbi from 'src/app/data/books/MainichiKinenbi';
import type { TBookMode } from '@studiobuki/web-core/lib/routing';
import { ROUTE_PATH_QUERY_PARAM_MODE_USER } from '@studiobuki/web-core/lib/routing';
import omit from 'lodash/omit';
import { BookPagesMainichiKinenbiService } from './book-pages-mainichi-kinenbi.service';

export type TBookMainichiKinenbiCreateData = Pick<
  IBookFroebelkanMainichiKinenbiData,
  'heroName' | 'kunChan' | 'coverColor'
> & {
  photo?: Blob;
};

const log = new Logger('BookMainichiKinenbiService');

@Injectable({
  providedIn: 'root',
})
export class BookMainichiKinenbiService extends BookService<
  IBookFroebelkanMainichiKinenbiData,
  TBookMainichiKinenbiCreateData
> {
  constructor(
    protected override _firebaseService: FirebaseService,
    protected override _bookPagesService: BookPagesMainichiKinenbiService,
    private _loaderService: LoaderService,
  ) {
    super(_firebaseService, _bookPagesService);
  }

  /** a method to create initial book data */
  // eslint-disable-next-line class-methods-use-this
  protected override _createBookData<
    O extends Partial<IBookFroebelkanMainichiKinenbiData>,
  >(
    /** data to create the book from */
    createBookData: TBookMainichiKinenbiCreateData,
    /** book data to owerride the defaults */
    overrideBookData: O,
  ) {
    const { kunChan } = createBookData;
    const newBook: Omit<
      IBookFroebelkanMainichiKinenbiData,
      TCreateUserBookOmitKeys
    > = {
      ...omit(createBookData, ['photo']),
      type: 'book',
      steps: {
        [INPUT_VALUE_STEP_COVER]: {
          stepType: INPUT_VALUE_STEP_COVER,
          isDone: true,
        },
        [INPUT_VALUE_STEP_ANNIVERSARY_1]: {
          stepType: INPUT_VALUE_STEP_ANNIVERSARY_1,
          isDone: false,
        },
        [INPUT_VALUE_STEP_ANNIVERSARY_2]: {
          stepType: INPUT_VALUE_STEP_ANNIVERSARY_2,
          isDone: false,
        },
        [INPUT_VALUE_STEP_ANNIVERSARY_3]: {
          stepType: INPUT_VALUE_STEP_ANNIVERSARY_3,
          isDone: false,
        },
        [INPUT_VALUE_STEP_ANNIVERSARY_4]: {
          stepType: INPUT_VALUE_STEP_ANNIVERSARY_4,
          isDone: false,
        },
        [INPUT_VALUE_STEP_ANNIVERSARY_5]: {
          stepType: INPUT_VALUE_STEP_ANNIVERSARY_5,
          isDone: false,
        },
        [INPUT_VALUE_STEP_ANNIVERSARY_6]: {
          stepType: INPUT_VALUE_STEP_ANNIVERSARY_6,
          isDone: false,
        },
        [INPUT_VALUE_STEP_ANNIVERSARY_7]: {
          stepType: INPUT_VALUE_STEP_ANNIVERSARY_7,
          isDone: false,
        },
        [INPUT_VALUE_STEP_PHOTO]: {
          stepType: INPUT_VALUE_STEP_PHOTO,
          isDone: false,
        },
        [INPUT_VALUE_STEP_MESSAGE]: {
          stepType: INPUT_VALUE_STEP_MESSAGE,
          isDone: false,
        },
      },
      multiFiles: {
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_0]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_1]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_2]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_3]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_4]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_5]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_6]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_7]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_8]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_9]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_10]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_11]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_12]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_13]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_14]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_15]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_16]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_17]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_18]: {
          fileUploadStatus: 0,
        },
        [PHOTO_FROEBELKAN_MAINICHI_KINENBI_19]: {
          fileUploadStatus: 0,
        },
      },
      alias: MainichiKinenbi.alias,
      cover: INPUT_VALUE_COVER_TYPE_SOFT,
      language: 'jp',
      anniversaryNames: [],
      anniversaryYears: [],
      anniversaryMonths: [],
      anniversaryDays: [],
      anniversaryMessages: [],
      backgroundColors: [],
      backgroundDesigns: [],
      photoframeDesigns: [],
      messageText: '',
    };

    return { ...newBook, ...overrideBookData };
  }

  override async createBook<
    O extends Partial<IBookFroebelkanMainichiKinenbiData>,
  >(createBookData: TBookMainichiKinenbiCreateData, overrideBookData?: O) {
    const bookId = await super.createBook(createBookData, overrideBookData);

    if (createBookData.photo) {
      await super.loadBook(bookId, () => {});

      await this.uploadMultiPhoto(
        createBookData.photo,
        PHOTO_FROEBELKAN_MAINICHI_KINENBI_0,
      );
    }

    return bookId;
  }

  override async loadBook(
    bookId: string,
    onBookDataLoad:
      | ((bookData: IBookFroebelkanMainichiKinenbiData) => Promise<void>)
      | ((bookData: IBookFroebelkanMainichiKinenbiData) => void),
    mode: TBookMode = ROUTE_PATH_QUERY_PARAM_MODE_USER,
  ) {
    const bookData = await super.loadBook(bookId, onBookDataLoad, mode);

    this._bookPagesService.newBookInit(bookData);

    const multiFilesEntries = Object.entries(bookData.multiFiles);
    this._loaderService.pushProgressMax(multiFilesEntries.length * 2);

    await Promise.all(
      multiFilesEntries.map(async ([photoName, data]) => {
        if (data.fileUploadStatus === 10) {
          log.info('loading', photoName);
          await this._multiFileLoad(photoName);
          log.info('loaded', photoName);
        }

        this._loaderService.pushProgressValue(2);
      }),
    );

    this.textChangeSubscribe.next();
    this.messageTextChangeSubscribe.next();

    return bookData;
  }
}
